import "@fontsource-variable/lora";
import "@fontsource-variable/onest";
import { StrictMode, useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type {
  CatalogTemplate,
  CreditPackageResponse,
  GenerationResponse,
  PaymentMethod,
} from "@nura/contracts";
import { createPaymentAttemptRegistry } from "../features/payment-session";
import { validatePhoto } from "../features/photo-picker";
import {
  CatalogScreen,
  ConfirmationScreen,
  PaymentsScreen,
  ProfileScreen,
  ProgressScreen,
  ResultScreen,
  TemplateDetailScreen,
  UploadScreen,
  WorksScreen,
  type PrimarySection,
} from "../pages/creation";
import { CatalogApiError, createCatalogApi } from "../shared/api";
import { connectTelegram, telegramInitData, telegramTheme } from "../shared/telegram";
import "../styles.css";

export const MINI_APP_ID = "nura-mini-app";

type Screen = PrimarySection | "detail" | "upload" | "confirmation" | "progress" | "result" | "payments";
type CatalogStatus = "loading" | "ready" | "error";

const api = createCatalogApi("/api", globalThis.fetch, telegramInitData);

function NuraApp() {
  const [screen, setScreen] = useState<Screen>("catalog");
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatus>("loading");
  const [templates, setTemplates] = useState<CatalogTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CatalogTemplate | undefined>(undefined);
  const [selectedPhoto, setSelectedPhoto] = useState<File | undefined>(undefined);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<{ generationId: string; downloadUrl: string; displayUrl: string } | undefined>(undefined);
  const [balance, setBalance] = useState<number | undefined>(undefined);
  const [history, setHistory] = useState<GenerationResponse[]>([]);
  const [historyPreviewUrls, setHistoryPreviewUrls] = useState<Record<string, string>>({});
  const [theme, setTheme] = useState<"light" | "dark">(() => telegramTheme());
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [toast, setToast] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [packages, setPackages] = useState<CreditPackageResponse[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const historyStack = useRef<Screen[]>([]);
  const screenRef = useRef<Screen>(screen);
  const generationTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const generationKey = useRef<string | undefined>(undefined);
  const paymentTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const paymentAttempts = useRef(createPaymentAttemptRegistry());
  const telegramRef = useRef<ReturnType<typeof connectTelegram> | undefined>(undefined);
  const historyPreviewUrlsRef = useRef<Record<string, string>>({});

  const showToast = useCallback((message: string) => {
    if (toastTimer.current !== undefined) clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(undefined), 3200);
  }, []);

  const navigate = useCallback((next: Screen) => {
    if (screenRef.current !== next) historyStack.current.push(screenRef.current);
    screenRef.current = next;
    setScreen(next);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const replaceScreen = useCallback((next: Screen) => {
    screenRef.current = next;
    setScreen(next);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const goBack = useCallback(() => {
    const next = historyStack.current.pop() ?? "catalog";
    screenRef.current = next;
    setScreen(next);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogStatus("loading");
    try {
      setTemplates(await api.listTemplates());
      setCatalogStatus("ready");
    } catch {
      setCatalogStatus("error");
    }
  }, []);

  const loadWallet = useCallback(async () => {
    try {
      const wallet = await api.getWallet();
      setBalance(wallet.availableCredits);
    } catch {
      // The public catalog stays available outside authenticated Telegram.
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const items = await api.listGenerations();
      setHistory(items);
      const previews = await Promise.all(items.map(async (item) => {
        const source = item.result?.previewUrl;
        if (item.status !== "succeeded" || source === undefined) return undefined;
        try {
          return [item.generationId, URL.createObjectURL(await api.readResult(source))] as const;
        } catch {
          return undefined;
        }
      }));
      const next = Object.fromEntries(previews.filter((entry) => entry !== undefined));
      for (const url of Object.values(historyPreviewUrlsRef.current)) URL.revokeObjectURL(url);
      historyPreviewUrlsRef.current = next;
      setHistoryPreviewUrls(next);
    } catch {
      // History is available only for an authenticated Telegram user.
    }
  }, []);

  useEffect(() => {
    const applyTheme = (theme: "light" | "dark") => {
      setTheme(theme);
      document.documentElement.dataset.theme = theme;
    };
    applyTheme(telegramTheme());
    telegramRef.current = connectTelegram(goBack, applyTheme);
    void loadCatalog();
    void loadWallet();
    void loadHistory();
    return () => {
      telegramRef.current?.dispose();
      if (generationTimer.current !== undefined) clearTimeout(generationTimer.current);
      if (paymentTimer.current !== undefined) clearTimeout(paymentTimer.current);
      if (toastTimer.current !== undefined) clearTimeout(toastTimer.current);
      for (const url of Object.values(historyPreviewUrlsRef.current)) URL.revokeObjectURL(url);
    };
  }, [goBack, loadCatalog, loadHistory, loadWallet]);

  useEffect(() => {
    telegramRef.current?.setBackVisible(!(["catalog", "works", "profile"] as Screen[]).includes(screen));
  }, [screen]);

  useEffect(() => () => {
    if (photoUrl !== undefined) URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);

  useEffect(() => () => {
    if (result?.displayUrl !== undefined) URL.revokeObjectURL(result.displayUrl);
  }, [result?.displayUrl]);

  function openTemplate(template: CatalogTemplate) {
    setSelectedTemplate(template);
    setFormError(undefined);
    telegramRef.current?.impact();
    navigate("detail");
  }

  function selectPhoto(file: File) {
    const error = validatePhoto(file);
    if (error !== undefined) {
      telegramRef.current?.error();
      setFormError(error === "file_too_large" ? "Фото больше 15 МБ" : "Выберите изображение JPG, PNG или WebP");
      return;
    }
    setFormError(undefined);
    setSelectedPhoto(file);
    setPhotoUrl(URL.createObjectURL(file));
    setResult(undefined);
    generationKey.current = undefined;
    telegramRef.current?.impact();
  }

  const finishGeneration = useCallback(async (response: GenerationResponse) => {
    if (response.status === "running") {
      generationTimer.current = setTimeout(async () => {
        try {
          await finishGeneration(await api.getGeneration(response.generationId));
        } catch {
          generationTimer.current = setTimeout(() => void finishGeneration(response), 3000);
        }
      }, 2200);
      return;
    }
    setSubmitting(false);
    if (response.status === "failed" || response.result === undefined) {
      generationKey.current = undefined;
      setFormError("Не удалось создать образ. Кредиты возвращены.");
      telegramRef.current?.error();
      replaceScreen("confirmation");
      void loadWallet();
      void loadHistory();
      return;
    }
    try {
      const blob = await api.readResult(response.result.displayUrl);
      const displayUrl = URL.createObjectURL(blob);
      setResult({ generationId: response.generationId,
        downloadUrl: response.result.downloadUrl, displayUrl });
      telegramRef.current?.success();
      replaceScreen("result");
    } catch {
      setFormError("Результат готов, но превью не загрузилось. Откройте его в «Моих работах».");
      replaceScreen("works");
    }
    void loadWallet();
    void loadHistory();
  }, [loadHistory, loadWallet, replaceScreen]);

  async function startGeneration() {
    if (selectedPhoto === undefined || selectedTemplate === undefined) return;
    if (balance !== undefined && balance < selectedTemplate.costCredits) {
      await openPayments();
      return;
    }
    setSubmitting(true);
    setFormError(undefined);
    generationKey.current ??= crypto.randomUUID();
    navigate("progress");
    try {
      await finishGeneration(await api.createGeneration(selectedTemplate.templateId, selectedPhoto, generationKey.current));
    } catch (error) {
      setSubmitting(false);
      generationKey.current = undefined;
      if (error instanceof CatalogApiError && error.code === "insufficient_credits") {
        setFormError("Недостаточно кредитов для этого образа.");
        replaceScreen("confirmation");
      } else if (error instanceof CatalogApiError && error.code === "too_many_generations") {
        setFormError("Сначала дождитесь завершения текущего образа.");
        replaceScreen("confirmation");
      } else {
        setFormError("Не удалось запустить создание. Попробуйте ещё раз.");
        telegramRef.current?.error();
        replaceScreen("confirmation");
      }
      void loadWallet();
    }
  }

  async function saveResult(sourceUrl: string, generationId: string) {
    try {
      const blob = await api.readResult(sourceUrl);
      const extension = blob.type === "image/png" ? "png" : blob.type === "image/webp" ? "webp" : "jpg";
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `nura-${generationId}.${extension}`;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      showToast("Результат сохранён");
    } catch {
      showToast("Не удалось сохранить результат. Попробуйте ещё раз.");
    }
  }

  async function loadPaymentOffers(force = false) {
    if ((!force && packages.length > 0) || paymentsLoading) return;
    setPaymentsLoading(true);
    try {
      const offers = await api.getCreditPackages();
      setPackages(offers.packages);
      setPaymentMethods(offers.methods);
    } catch {
      showToast("Не удалось загрузить способы оплаты.");
    } finally {
      setPaymentsLoading(false);
    }
  }

  async function openPayments() {
    navigate("payments");
    await loadPaymentOffers();
  }

  const pollPayment = useCallback(async (paymentId: string) => {
    if (!paymentAttempts.current.nextPoll(paymentId)) {
      paymentAttempts.current.pause(paymentId);
      setPaymentBusy(false);
      showToast("Платёж ещё не подтверждён. Статус можно проверить позже.");
      return;
    }
    try {
      const payment = await api.getPayment(paymentId);
      if (payment.status === "paid") {
        paymentAttempts.current.release(paymentId);
        setPaymentBusy(false);
        telegramRef.current?.success();
        showToast(`${payment.credits} кредитов зачислены`);
        await loadWallet();
        replaceScreen(selectedPhoto !== undefined && selectedTemplate !== undefined ? "confirmation" : "profile");
        return;
      }
      if (payment.status === "cancelled") {
        paymentAttempts.current.release(paymentId);
        setPaymentBusy(false);
        showToast("Оплата отменена. Можно выбрать пакет снова.");
        return;
      }
    } catch {
      // Provider confirmation may still be on the way.
    }
    paymentTimer.current = setTimeout(() => void pollPayment(paymentId), 2000);
  }, [loadWallet, replaceScreen, selectedPhoto, selectedTemplate, showToast]);

  async function buyCredits(packageCode: CreditPackageResponse["code"], method: PaymentMethod) {
    const attempt = paymentAttempts.current.prepare(packageCode, method);
    setPaymentBusy(true);
    try {
      const payment = await api.createPayment(packageCode, method, attempt.idempotencyKey);
      if (payment.checkoutUrl === undefined) throw new Error("checkout unavailable");
      paymentAttempts.current.activate(attempt.cacheKey, payment.paymentId);
      if (method === "telegram_stars") {
        const status = await telegramRef.current!.openInvoice(payment.checkoutUrl);
        if (status === "cancelled" || status === "failed") {
          await api.cancelPayment(payment.paymentId);
          paymentAttempts.current.release(payment.paymentId);
          setPaymentBusy(false);
          showToast("Оплата отменена. Можно выбрать пакет снова.");
          return;
        }
      } else {
        telegramRef.current?.openExternal(payment.checkoutUrl);
      }
      void pollPayment(payment.paymentId);
    } catch {
      const activeId = paymentAttempts.current.activePaymentId();
      if (activeId !== undefined) paymentAttempts.current.pause(activeId);
      setPaymentBusy(false);
      telegramRef.current?.error();
      showToast("Не удалось открыть оплату. Попробуйте ещё раз.");
    }
  }

  function navigatePrimary(section: PrimarySection) {
    historyStack.current = [];
    replaceScreen(section);
  }

  let content;
  if (screen === "catalog") {
    content = <CatalogScreen balance={balance} onNavigate={navigatePrimary} onOpenBalance={() => void openPayments()} onOpenTemplate={openTemplate} onRetry={() => void loadCatalog()} status={catalogStatus} templates={templates} />;
  } else if (screen === "detail" && selectedTemplate !== undefined) {
    content = <TemplateDetailScreen onBack={goBack} onChoosePhoto={() => navigate("upload")} template={selectedTemplate} />;
  } else if (screen === "upload" && selectedTemplate !== undefined) {
    content = <UploadScreen balance={balance} error={formError} onBack={goBack} onContinue={() => navigate("confirmation")} onOpenBalance={() => void openPayments()} onSelectPhoto={selectPhoto} photoUrl={photoUrl} template={selectedTemplate} />;
  } else if (screen === "confirmation" && selectedTemplate !== undefined && photoUrl !== undefined) {
    content = <ConfirmationScreen balance={balance} error={formError} onBack={goBack} onChangePhoto={() => replaceScreen("upload")} onConfirm={() => void startGeneration()} photoUrl={photoUrl} submitting={submitting} template={selectedTemplate} />;
  } else if (screen === "progress" && selectedTemplate !== undefined && photoUrl !== undefined) {
    content = <ProgressScreen onOpenWorks={() => navigatePrimary("works")} photoUrl={photoUrl} template={selectedTemplate} />;
  } else if (screen === "result" && selectedTemplate !== undefined && result !== undefined) {
    content = <ResultScreen onAnotherTemplate={() => navigatePrimary("catalog")} onBack={() => navigatePrimary("catalog")} onReportProblem={() => showToast("Напишите в поддержку из раздела «Профиль»")} onSave={() => void saveResult(result.downloadUrl, result.generationId)} resultUrl={result.displayUrl} template={selectedTemplate} />;
  } else if (screen === "works") {
    content = <WorksScreen history={history} onNavigate={navigatePrimary} onSave={(item) => item.result !== undefined && void saveResult(item.result.downloadUrl, item.generationId)} previewUrls={historyPreviewUrls} templates={templates} />;
  } else if (screen === "profile") {
    content = <ProfileScreen balance={balance} onNavigate={navigatePrimary} onOpenPayments={() => void openPayments()} onTheme={(nextTheme) => { setTheme(nextTheme); document.documentElement.dataset.theme = nextTheme; telegramRef.current?.setTheme(nextTheme); }} theme={theme} />;
  } else if (screen === "payments") {
    content = <PaymentsScreen busy={paymentBusy} loading={paymentsLoading} methods={paymentMethods} onBack={goBack} onBuy={(code, method) => void buyCredits(code, method)} onRetry={() => { setPackages([]); void loadPaymentOffers(true); }} packages={packages} />;
  } else {
    content = <CatalogScreen balance={balance} onNavigate={navigatePrimary} onOpenBalance={() => void openPayments()} onOpenTemplate={openTemplate} onRetry={() => void loadCatalog()} status={catalogStatus} templates={templates} />;
  }

  return <div className="mini-app-shell">{content}<div aria-live="polite" className={toast === undefined ? "toast" : "toast toast--visible"} role="status">{toast}</div></div>;
}

const root = document.querySelector<HTMLElement>("#app");
if (root === null) throw new Error("Mini App root not found");
const appWindow = window as Window & { __nuraReactRoot?: ReturnType<typeof createRoot> };
const reactRoot = appWindow.__nuraReactRoot ?? createRoot(root);
appWindow.__nuraReactRoot = reactRoot;
reactRoot.render(<StrictMode><NuraApp /></StrictMode>);
